/**
 * Bringing one flight scene up: the asynchronous half of `createFlightScene`.
 *
 * Everything between "the renderer exists" and "the loop is running and the
 * status says ready" — device init, the first viewport sizing, the player's
 * airframe and gun mounts, the region and its ground query, the pipeline
 * precompile, and the device-loss watch that outlives all of it.
 *
 * Split out of `flight-scene.ts` for the reason `scene-draw.ts`,
 * `scene-particles.ts`, `scene-propellers.ts`, `scene-killcam.ts` and
 * `scene-flyby-zoom.ts` were: the host is at aislop's 400-line ceiling, and the
 * boot path is the largest block in it that is cohesive on its own terms — it
 * is the only code in the file that is `await`ed, it is the only code that has
 * to re-check `state.disposed` after every step, and nothing in the frame loop
 * calls into it.
 *
 * The host is described **structurally** (`BootHost`) rather than by importing
 * `scene-loop.ts`'s `SceneState` — the same arrangement every other sibling
 * here uses, and the reason it is still worth having: this module needs a
 * *subset*, and saying which subset is what stops it from quietly growing a
 * dependency on the whole scene. The one thing this module cannot supply for
 * itself is the frame loop, because that closes over `onFrame` and the
 * callbacks; it is handed in as `startLoop`.
 */

import * as THREE from "three/webgpu";
import type { GroundQuery, MissionRegion, SceneryQuery, SkyCondition, TimeOfDay } from "@red-opus/engine";
import type { PersonalPaint } from "@red-opus/formats";
import { disposeTree } from "../three/dispose-tree.ts";
import { setModelledPropellerVisible } from "../three/enhanced-nodes.ts";
import type { BytesProgress } from "../three/streamed-body.ts";
import { modelledPropellerVisible } from "./modelled-propeller.ts";
import { precompileScene } from "../three/precompile-scene.ts";
import { applyTransparentSort } from "../three/transparent-sort.ts";
import { measureView, type ViewMetrics } from "../three/view-metrics.ts";
import { applyViewPixelRatio } from "../three/view-pixel-ratio.ts";
import { watchDeviceLoss } from "../three/webgpu-support.ts";
import { loadAircraftMesh } from "./aircraft-mesh.ts";
import type { CockpitInterior } from "../three/cockpit-interior.ts";
import { createBootReporter } from "./boot-reporter.ts";
import { fallBackAfterDeviceLoss, openFlightRenderer, reportViewportHealth } from "./scene-renderer-boot.ts";
import type { RendererPlan } from "../three/renderer-mode.ts";
import { loadFlightRegion } from "./fly-region.ts";
import { applyRegionRelief, type TerrainSurface } from "./scene-terrain.ts";
import { groundRampShiftForColour } from "./time-of-day.ts";
import { dressingGroundColour, type SkyDressing } from "./sky-dressing.ts";
import { skyTreatmentFor, type EffectsSettings, type SkyTreatment } from "./effects-settings.ts";
import type { FlightSceneCallbacks } from "./flight-scene-api.ts";
import { CAMERA_FOV_DEG, resizeView } from "./flight-view.ts";
import { loadPropellers, type PropellerHost } from "./scene-propellers.ts";
import { playerAircraftKey, type CameraOwner } from "./scene-camera.ts";
import { updateMuzzleViewport, type CockpitReportState } from "./scene-cockpit-report.ts";
import type { SpriteEffects } from "./sprite-effects.ts";

/**
 * What booting a scene needs from `flight-scene.ts`'s own state — the union of
 * what the propellers, the camera and the muzzle viewport each already ask for,
 * plus the four things only this path touches.
 */
export interface BootHost extends PropellerHost, CockpitReportState, CameraOwner {
  /** Written exactly once by `openFlightRenderer` below, before anything else touches it — see `SceneState.renderer`. */
  renderer: THREE.WebGPURenderer;
  /** The backend decision this boot is executing (`three/renderer-mode.ts`). */
  readonly rendererPlan: RendererPlan;
  readonly sprites: SpriteEffects;
  readonly playerPaint: PersonalPaint | undefined;
  /** Written once the region's instancer is ready — see `flight-scene.ts`'s own field doc. CSS pixels. */
  setTerrainLineWidth: (viewWidthCssPx: number) => void;
  /** Sky, ground plane and sun; the terrain step sinks its backdrop below the relief. */
  readonly dressing: SkyDressing;
  /** Which port extensions are on — the terrain step reads `enhancedTerrain` and `enhancedSky`. */
  readonly effects: EffectsSettings;
  /** The mission's briefed hour, or `undefined` for a dogfight/duel — dims the terrain to match the sky. */
  readonly timeOfDay: TimeOfDay | undefined;
  /** The mission's briefed sky word; outside the `measured` reference it darkens the ground as well as the dome. */
  readonly skyWord: SkyCondition | undefined;
  /** `?treatment=<name>`'s debug pin, which beats `effects.enhancedSky` when it is set. */
  readonly skyOverride: SkyTreatment | undefined;
  /** The region's ground surface and its decoration, filled here (`scene-terrain.ts`). */
  terrain?: TerrainSurface;
  /** Last-request-wins guard for `loadAircraft`, which reruns whenever ENHANCED MODELS is flipped. */
  aircraftGeneration: number;
  /** The swappable mesh's own group inside the pose group — see `installAircraftMesh`. */
  readonly aircraftModel: THREE.Group;
  /**
   * 3D COCKPIT's interior treatment (`three/cockpit-interior.ts`), handed to
   * every material this path builds for the player so an ENHANCED MODELS swap
   * cannot leave the new aeroplane without it.
   */
  readonly cockpitInterior: CockpitInterior;
}

/**
 * The drawing buffer, and everything sized against the view, brought back into
 * agreement with the container.
 *
 * One function because it has **two** callers that must not be able to drift
 * apart: the container's `ResizeObserver` (`flight-scene.ts`) and the ENHANCED
 * MODELS resolution switch below.
 *
 * All three lines are in **CSS pixels**, which is the interesting part and the
 * reason the switch is safe. A terrain hairline is one *original* pixel of the
 * view (`three/polygon-outlines.ts`'s `originalPixelWidth`) and three consumes
 * that width in CSS pixels, so `getSize` is the right question and
 * `getDrawingBufferSize` is the wrong one — feed it the buffer and every road
 * thickens with the pixel ratio, which is exactly what a swapchain measurement
 * of the ratio-2 and ratio-3 pictures caught (8.3 CSS px of road against 13.7).
 * With the view's own width, a sharper buffer draws the *same* road with more
 * samples, which is the whole point of the setting.
 *
 * A no-op on `setTerrainLineWidth` until the region loads, which is why the boot
 * path below can call it before there is any terrain.
 */
/**
 * Scratch for `renderer.getSize`, which writes into a caller-supplied vector.
 *
 * Module scope rather than per call for `scene-killcam.ts`'s reason: a resize
 * burst is one notification per animation frame while a window is being
 * dragged, so a `new THREE.Vector2()` in here is an allocation per frame for as
 * long as the drag lasts, at exactly the moment the renderer is busiest.
 */
const SCRATCH_SIZE = new THREE.Vector2();

export function resizeViewport(host: BootHost, measured?: ViewMetrics): void {
  // One measurement, taken here or handed in by the observer that already took
  // it, and then passed down by value. Every consumer below is sized against
  // the *same* numbers — which is the whole point of the function, and used not
  // to be true of it: `resizeView` and the dash quad each read the container
  // for themselves.
  const metrics = measured ?? measureView(host.container);
  // The drawing-buffer scale first, because `setPixelRatio` re-runs `setSize`
  // with whatever size three last had, and the sizing below must be what wins.
  // This is the half a `ResizeObserver` cannot see: a window dragged to a
  // different-density monitor changes `devicePixelRatio` and *nothing else*, so
  // without this line the buffer keeps the density it booted with for the rest
  // of the session (`three/view-metrics.ts`).
  applyViewPixelRatio(host.renderer, host.effects.enhancedModels, metrics.devicePixelRatio);
  resizeView(host.renderer, host.rig, metrics);
  updateMuzzleViewport(host, metrics);
  // 3D COCKPIT's dashboard quad is a screen registration against the painted
  // panel, exactly like the muzzle viewport above and for the same reason — the
  // panel's integer scale and bottom-centre anchor put it somewhere different in
  // the frustum on every window size (`cockpit-dash-quad.ts`).
  // The same three numbers `updateMuzzleViewport` uses, and the same `CAMERA_FOV_DEG`
  // rather than `camera.fov`: the shear grows the *virtual* frame's angle while
  // the rendered sub-window keeps subtending the base one (`cockpit-projection.ts`),
  // and it is the rendered one both registrations are measured in.
  host.dashQuad?.setViewport({
    widthPx: metrics.widthCssPx,
    heightPx: metrics.heightCssPx,
    fovDeg: CAMERA_FOV_DEG,
  });
  host.setTerrainLineWidth(host.renderer.getSize(SCRATCH_SIZE).x);
}

/**
 * Puts a freshly loaded player mesh in, and frees the one it replaces.
 *
 * **`aircraftModel`, not `aircraft`** — and the distinction is the whole reason
 * this is its own function. The pose group carries more than the aeroplane's
 * mesh: 3D COCKPIT's dashboard quad hangs off it too, so that the dash turns
 * with the aeroplane. Emptying and disposing the pose group therefore does not
 * only swap the model, it **detaches the dash quad and disposes its material and
 * geometry** — which is exactly what happened, on the boot path's own first
 * call, for the whole of 3D COCKPIT phase 1: the quad went on reporting itself
 * visible, mapped and freshly uploaded from a node that was no longer in the
 * scene, and the dashboard drew nothing. Anything that must survive an ENHANCED
 * MODELS swap belongs on `aircraft`; only the mesh belongs in here.
 */
export function installAircraftMesh(host: Pick<BootHost, "aircraftModel">, mesh: THREE.Object3D): void {
  disposeTree(host.aircraftModel);
  host.aircraftModel.clear();
  host.aircraftModel.add(mesh);
}

/**
 * The player's own aeroplane, loaded into its own group inside the pose group.
 *
 * Exported because it is called **again** whenever ENHANCED MODELS changes
 * (`flight-scene.ts`'s `setEffectsSettings`), which is why `installAircraftMesh`
 * clears first: that switch swaps the *geometry*, so the aeroplane already in
 * there has to be freed rather than hidden. `aircraftGeneration` is the same
 * last-request-wins guard the career turntable uses — two flips in quick
 * succession must not race a slow fetch into a group that has since been
 * emptied and refilled.
 */
export async function loadAircraft(host: BootHost, onBytes?: BytesProgress): Promise<void> {
  const generation = ++host.aircraftGeneration;
  const mesh = await loadAircraftMesh(host.playerModelId, {
    paint: host.playerPaint,
    enhanced: host.effects.enhancedModels,
    // The scene owns a `PropellerDiscs`, so the enhanced `.glb`'s own blades are
    // only drawn where that layer is not drawing any (`modelled-propeller.ts`).
    propellerEffect: host.effects.propellerEffect,
    // 3D COCKPIT's interior treatment, on the player's material and no other.
    // Passed unconditionally rather than behind `effects.cockpit3d`, because
    // the setting flips mid-flight without a reload (see below) and a material
    // that had to be rebuilt for it would compile a pipeline on that frame. The
    // gate is a uniform inside the object, and at rest it changes nothing.
    cockpitInterior: host.cockpitInterior,
    // Only the boot passes one — a mid-flight toggle flip has no loading screen
    // to report to.
    onBytes,
  });
  if (!mesh || host.disposed || generation !== host.aircraftGeneration) return;
  installAircraftMesh(host, mesh);
}

/**
 * What flipping the toggle row mid-flight needs over and above booting: the
 * `effects` field is *written* here (the one place it moves after construction),
 * and the fleet's other aeroplanes swap through `combat`.
 */
export interface EffectsToggleHost extends BootHost {
  effects: EffectsSettings;
  readonly combat: {
    setEnhancedModels(enabled: boolean): void;
    setEnhancedTracers(enabled: boolean): void;
    setPropellerEffect(enabled: boolean): void;
  };
}

/**
 * Applies a new toggle-row state to a flying scene — the write half of the
 * `EffectsSettings` the boot path above reads. It lives beside `loadAircraft`
 * because ENHANCED MODELS is the one toggle that swaps geometry rather than a
 * flag, and this is its only other caller. The sky and the ground dim
 * together or the picture pulls apart — both take the *same* colour,
 * `dressingGroundColour`, so the flat backdrop and the relief in front of it
 * cannot walk the ramp by different amounts under a sky word.
 */
export function applyEffectsSettings(host: EffectsToggleHost, settings: EffectsSettings): void {
  const swapModels = settings.enhancedModels !== host.effects.enhancedModels;
  host.effects = settings;
  const treatment = skyTreatmentFor(settings, host.skyOverride);
  host.propellers?.setEnabled(settings.propellerEffect);
  // PROPELLER EFFECT is the *other* half of one decision: switching the disc
  // layer off uncovers the propeller the enhanced `.glb` was built with, and
  // switching it on hides it again. A flag on a node, so neither direction
  // reloads anything — and it is applied to the aeroplanes already in the sky
  // as well as through the load below, which is why the fleet gets its own call.
  setModelledPropellerVisible(host.aircraft, modelledPropellerVisible(host.playerModelId, settings.propellerEffect));
  host.combat.setPropellerEffect(settings.propellerEffect);
  host.dressing.setSkyTreatment(treatment);
  host.terrain?.setHourShift(
    groundRampShiftForColour(dressingGroundColour(host.timeOfDay, treatment, host.skyWord)),
  );
  host.terrain?.setDecoration(settings.enhancedTerrain);
  // The ground half of ENHANCED MODELS. Unconditional rather than inside the
  // `swapModels` branch below, for the same reason `setDecoration` is: it is a
  // geometry *assignment* per distinct building, idempotent, and cheaper than
  // the comparison that would guard it.
  host.terrain?.setEnhancedStructures(settings.enhancedModels);
  // 3D COCKPIT needs nothing here at all, which is worth saying rather than
  // leaving to be noticed: `scene-camera.ts` re-reads `host.effects` on every
  // frame it decides the mesh, the pilot and the quad from, so flipping the
  // switch mid-flight takes effect on the next frame with no reload, no
  // geometry swap and no cache to invalidate. The one thing it *does* need is
  // ENHANCED MODELS to have swapped first when both change at once, which is
  // the order this function already runs in (`cockpit3dDrawn` reads the
  // settings, `loadAircraft` below reads the same object).
  // ENHANCED EFFECTS owns the gunfire as well as the fire and smoke: on, rounds
  // are `three/tracer-pool.ts`'s hot head and glowing tail; off, they are the
  // original's own `BULLETS.BMP` dashes, unchanged. A flag, so no rebuild.
  host.combat.setEnhancedTracers(settings.enhancedEffects);
  if (swapModels) {
    host.combat.setEnhancedModels(settings.enhancedModels);
    void loadAircraft(host);
    // ENHANCED MODELS owns the *resolution* as well as the geometry
    // (`three/view-pixel-ratio.ts`): on, the buffer is the display's own
    // `devicePixelRatio`; off, it is capped at 2. Live, like everything else on
    // this panel — and it goes through the same one function a window resize
    // does, which now applies the pixel ratio itself from `host.effects`
    // (reassigned at the top of this function, so it is already the new
    // setting). Unconditional rather than gated on the ratio having moved: the
    // gate saved one fan-out on a settings toggle and was a second place that
    // had to know what `resizeViewport` depends on.
    resizeViewport(host);
  }
}

/**
 * Runs the whole boot sequence and resolves when the scene is either flying or
 * has reported an error. Never rejects — every failure path goes through
 * `callbacks.onStatus("error", …)`, because the caller keeps this promise only
 * to sequence `renderer.dispose()` behind it.
 */
export function watchInit(
  host: BootHost,
  callbacks: FlightSceneCallbacks,
  /** Swap target for the delegating ground query, or `null` when the caller injected its own (see `createFlightScene`). */
  groundRef: { current: GroundQuery } | null,
  /** Swap target for the delegating scenery query (AD-018), on the same terms as `groundRef`. */
  sceneryRef: { current: SceneryQuery } | null,
  /** Which `MissionRegion` to load — see `FlightSceneOptions.region`'s doc for how this is decided. */
  flyRegion: MissionRegion | undefined,
  /** Starts the frame loop. Supplied by the host because it closes over `onFrame` and the callbacks. */
  startLoop: () => void,
  /**
   * Called with the region's own ground in the same breath as the swap below,
   * and like it strictly before the first tick. `flight-scene.ts` uses it to
   * reseat a mission's ground-started flights on the terrain that is really
   * under them — until this line the delegating query still answers flat datum
   * (`sim-runner.ts`'s `MissionRunner.reseat`).
   */
  onGroundReady?: (ground: GroundQuery) => void,
): Promise<void> {
  const reporter = createBootReporter(callbacks);
  reporter.phase("device");
  return openFlightRenderer(host, reporter, callbacks)
    .then(async () => {
      if (host.disposed) return;
      // Here and not in `buildRenderer` because the backend only settles
      // `reversedDepthBuffer` during `init()` — see `three/transparent-sort.ts`
      // for what the renderer's reversed-Z does to every `renderOrder` in this
      // scene, and why reading three's own flag rather than the requested one
      // is what keeps the `?renderer=webgl` capture hatch honest.
      applyTransparentSort(host.renderer);
      resizeViewport(host);
      // Strictly after the first `resizeViewport`, because "the buffer is still
      // three's 300x150 default" is only a fault once something was supposed to
      // have sized it. See `reportViewportHealth`.
      reportViewportHealth(host);
      // Worked out before anything is fetched, because the region wants it and
      // the region fetch is started here: a placed face's colour is baked into
      // shared geometry, so the hour has to be known when it is made. The *same*
      // colour the backdrop plane is painted (`dressingGroundColour`), so the
      // flat ground and the relief in front of it walk the ramp together.
      const hourShift = groundRampShiftForColour(
        dressingGroundColour(host.timeOfDay, skyTreatmentFor(host.effects, host.skyOverride), host.skyWord),
      );
      // **Started here, awaited below.** These three fetch from three different
      // places — the cockpit's `.TTM` composition, the player's airframe, and
      // the region's `.WLD` body with its ~50 placed models — and none of them
      // reads anything another writes, so running them in sequence made the
      // boot the *sum* of three independent waits rather than the longest of
      // them. On a 20 Mbit link that was 1.6 s of the 3.9 s cold boot
      // (`docs/engineering/fly-boot-timing.md` §3).
      //
      // What is **not** parallelised is the order things are put into the
      // scene: every `await` below is in the original sequence and every
      // mutation still happens behind it, so the scene graph is assembled
      // child-for-child as it always was — the propeller discs before the
      // region's group, the ground and scenery swapped in before the first
      // tick. Determinism is a property of that order, not of the fetches: the
      // region body, its model set and its origin are the same objects whatever
      // order the responses arrive in, because `loadFlightRegion` awaits its own
      // inputs and `regionGroundFrom` is pure.
      const regionPending = loadFlightRegion(flyRegion, host.renderer.getSize(new THREE.Vector2()).x, hourShift, { timeOfDay: host.timeOfDay, sky: host.skyWord });
      const muzzlePending = host.muzzles.load(playerAircraftKey(host));
      // The airframe's own download, reported live — the loading bar's one
      // genuinely measured input, and worth having because on a thin link this
      // one file is 65 % of everything a cold boot fetches
      // (`docs/engineering/fly-boot-timing.md` §6). It starts here, before the
      // `airframe` phase is announced, so `boot-reporter.ts` holds the counts
      // until the caption names the aircraft.
      const aircraftPending = loadAircraft(host, (loaded, total) => reporter.measure("airframe", loaded, total));
      // A rejection that arrives before its `await` must not be reported as
      // unhandled. Attaching a sink leaves the original promise rejected, so the
      // `await` below still throws into the boot's own `catch`.
      for (const pending of [regionPending, muzzlePending, aircraftPending]) pending.catch(() => undefined);
      reporter.phase("guns");
      await muzzlePending;
      reporter.phase("airframe");
      await aircraftPending;
      reporter.phase("propellers");
      await loadPropellers(host);
      reporter.phase("region");
      const region = await regionPending;
      if (host.disposed) {
        disposeTree(region.instances.group);
        return;
      }
      reporter.phase("terrain");
      host.scene.add(region.instances.group);
      // The region's ground surface, and — only if the setting asks for it —
      // the decoration drawn on it (`scene-terrain.ts`). Both go in before the
      // precompile below, so a toggle flip later costs no pipeline compile.
      host.terrain = applyRegionRelief(host.dressing, region);
      host.terrain.setHourShift(hourShift);
      host.terrain.setDecoration(host.effects.enhancedTerrain);
      // The buildings, which the instancer built both ways and left on the bake:
      // the boot has to state the setting once, exactly as it does for the
      // decoration, or the switch reads ON over an authentic town.
      host.terrain.setEnhancedStructures(host.effects.enhancedModels);
      host.sprites.setScenery(region.instances.billboards);
      host.setTerrainLineWidth = region.instances.setViewWidth;
      // **Re-sync, because until this line the setter was a no-op stub.** The
      // width the hairlines were built with was `renderer.getSize().x` as it
      // stood when the region fetch *started* (above), and `setTerrainLineWidth`
      // was `flight-scene.ts`'s `() => {}` for the whole of that fetch — several
      // seconds on a cold boot (`docs/engineering/fly-boot-timing.md`). Every
      // resize that arrived in that window reached the renderer, the camera and
      // both cockpit registrations and was **silently dropped by the terrain**,
      // leaving every road and field edge at the boot width for the rest of the
      // flight. Nothing re-applied it afterwards, because a `ResizeObserver`
      // only fires on a change and the size had already finished changing.
      resizeViewport(host);
      callbacks.onRegion(region.summary);
      if (groundRef) groundRef.current = region.ground;
      // Beside the swap, for the same "no tick may see it" reason, and after it
      // so a reseat that reads the delegating query gets the region's ground.
      onGroundReady?.(region.ground);
      // Swapped in beside the ground, strictly before the first tick, for the
      // reason `sim-runner.ts`'s note gives: no sim tick may ever see the swap.
      if (sceneryRef) sceneryRef.current = region.scenery;
      // Precompile every render pipeline while the loading status is still up.
      // Without this the first render() creates them synchronously — WGSL→native
      // compilation on the main thread — a visible hitch at the moment the
      // picture appears (user report, 2026-08-21). Not `compileAsync` directly:
      // that one only reaches what is in front of the camera and switched on,
      // leaving the rest to compile on the frame they first come into view
      // (user report, 2026-08-23). `three/precompile-scene.ts` has the detail.
      reporter.phase("precompile");
      // Reported per chunk, because this phase dominates a boot once the bytes
      // are local and a single await over it can say nothing while it runs
      // (`docs/engineering/fly-boot-timing.md` §6e).
      const onCompiled = (done: number, total: number) => reporter.measure("precompile", done, total);
      await precompileScene(host.renderer, host.scene, host.rig.camera, onCompiled);
      if (host.disposed) return;
      startLoop();
      performance.mark("fly-boot:ready");
      callbacks.onStatus("ready");
      // A device lost *after* a successful boot is not an error the player can
      // do anything about, and it is not something this scene can survive:
      // every material, texture and compiled pipeline in it belongs to the dead
      // device. `three/renderer-open.ts` explains why a live rebuild is out of
      // scope and what the reload does and does not keep; the honest thing to do
      // here is say so and come back on WebGL.
      watchDeviceLoss(host.renderer, (info) => {
        if (host.disposed) return;
        fallBackAfterDeviceLoss(info, callbacks);
      });
    })
    .catch((error: unknown) => {
      if (host.disposed) return;
      callbacks.onStatus("error", error instanceof Error ? error.message : String(error));
    });
}
